import { getAvailableFilters, getTopLevelOptions } from 'helpers/filterComponent.helper/filterComponent.helper'
import { getRoute } from 'helpers/route.helper/route.helper'
import { useActiveTags } from 'hooks/filterHooks/filterHooks'
import React, { ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { initGroupingPerRoute, setGrouping } from 'store/filter/filter'
import { removeFilterFromUrl } from 'store/filter/urlFilter.helper'
import NewButton from 'ui/atoms/NewButton/NewButton'
import { systemFeatureFlagActive } from 'utils/systemUtils'
import { getPrettyName } from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView.helper'
import { useAuth } from '../AuthContext/AuthContext'
import InfoBox from '../ModuleHeader/InfoBox'
import FilterDropdown from './FilterDropdown'
import FilterTour from './FilterTour'
import SavedFilterDropdown from './SavedFilterDropdown'

type FilterDropdownsGroupProps = {
  options: Record<string, string[]> // All available options, including those that are not currently selected.
  translations?: { [key: string]: string }
  onSelected: (topLevelTag: string, value: Record<string, boolean>) => void
  values?: string[] // The currenly selected options (e.g. ['top_level_a/sub_level_a', 'top_level_a/sub_level_b', ...]). When nothing is filtered all values should be included since it should show all data.
  hiddenFilters?: string[] // Filters that should be hidden (e.g. ['unit', 'fuel/No Fuel', ...])
  topLevelSortingOrder?: string[] // Determines the order of the top level tags. Tags that are not in this list are sorted alphabetically at the end.
  availableFiltersSubset: string[][] // A collection of tags for each of the items that are filtered by this component to enable "filtering of filters".
}

export default function FilterDropdownsGroup({
  options,
  translations,
  onSelected,
  values = [],
  hiddenFilters = [],
  topLevelSortingOrder = [],
  availableFiltersSubset,
}: FilterDropdownsGroupProps): ReactElement {
  const route = getRoute()
  const { t } = useTranslation()
  const [runTour, setRunTour] = useState<null | number>(null)
  const navigate = useNavigate()
  const { activeSystem } = useAuth()

  const { checkedFiltersPerDropdown, isActiveTagsFiltered } = useActiveTags({ availableFiltersSubset })
  const availableFilters = useMemo(
    () => getAvailableFilters(availableFiltersSubset, values, new Set(hiddenFilters), checkedFiltersPerDropdown),
    [availableFiltersSubset, values, hiddenFilters, checkedFiltersPerDropdown]
  )
  const topLevelOptions = useMemo(() => {
    const allTopLevelOptions = getTopLevelOptions(availableFilters, options, hiddenFilters)
    const flattenedLegendTags = availableFiltersSubset.flat()

    // Make sure that the topLevelOptions only include those that should be shown for that module
    // Fix due to checkedFilterStore are shared through modules
    return allTopLevelOptions.filter((option) => {
      return flattenedLegendTags.some((tag) => tag.startsWith(`${option.label}/`))
    })
  }, [availableFilters, hiddenFilters, options, availableFiltersSubset])

  const handleReset = useCallback(() => {
    removeFilterFromUrl(navigate)

    const routeKey = route as keyof typeof initGroupingPerRoute
    if (routeKey in initGroupingPerRoute) {
      setGrouping(initGroupingPerRoute[routeKey], navigate)
    }
  }, [navigate, route])

  const hasSavedFilterFeature = systemFeatureFlagActive(activeSystem, 'saved_filter_component')

  return (
    <div className="flex flex-wrap gap-2 filter_group mb-2" data-testid="filter-group">
      {/* e2e: filter-group is the saved-filters container selector — do not remove */}
      {hasSavedFilterFeature && <SavedFilterDropdown isActiveTagsFiltered={isActiveTagsFiltered} />}
      {topLevelOptions
        .sort((a, b) => compareTopLevelTags(a.label, b.label, topLevelSortingOrder, translations))
        .map((item, index) => (
          <FilterDropdown
            key={`${item.label}-${index}`}
            onApply={onSelected}
            label={item.label}
            options={options[item.label]}
            translations={translations}
            hiddenFilters={hiddenFilters}
            availableFilters={availableFilters}
            initialCheckedFilters={checkedFiltersPerDropdown[item.label] ?? {}}
          />
        ))}
      {isActiveTagsFiltered && <NewButton variant="outlined" label={t('Reset all filters')} onClick={handleReset} />}
      <FilterTour runTour={runTour} setRunTour={(state: boolean) => setRunTour(state ? 0 : null)} />
      <span className="flex items-center">
        <InfoBox onInfoButtonClick={() => setRunTour(0)} wantToLearnMoreText={t('How does the filtering work?')} />
      </span>
    </div>
  )
}

// Used to sort top level tags according to the order in sortingOrder. Tags that are not in sortingOrder are sorted alphabetically at the end.
function compareTopLevelTags(
  labelA: string,
  labelB: string,
  sortingOrder: string[],
  translations?: { [key: string]: string }
): number {
  if (sortingOrder.includes(labelA) && sortingOrder.includes(labelB)) {
    return sortingOrder.indexOf(labelA) - sortingOrder.indexOf(labelB)
  } else if (sortingOrder.includes(labelA)) {
    return -1
  } else if (sortingOrder.includes(labelB)) {
    return 1
  } else {
    return getPrettyName(labelA, translations).localeCompare(getPrettyName(labelB, translations))
  }
}
